<template>
  <PageLayout :title="$t('calendar.title')" :subtitle="$t('calendar.subtitle')">
    <template #actions>
      <router-link to="/posts/create" class="btn btn-primary">
        <i class="fas fa-plus mr-1"></i> {{ $t('posts.new_post') }}
      </router-link>
    </template>

    <div class="row">
      <div class="col-lg-8 mb-30">
        <ContentCard>
          <div class="cal-toolbar">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="shiftMonth(-1)">
              <i class="fas fa-chevron-left"></i>
            </button>
            <div class="cal-toolbar__title">
              <h5 class="mb-0">{{ monthLabel }}</h5>
              <button type="button" class="btn btn-link btn-sm p-0" @click="goToday">{{ $t('common.today') }}</button>
            </div>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="shiftMonth(1)">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>

          <div class="cal-weekdays">
            <div v-for="name in weekdayNames" :key="name" class="cal-weekday">{{ name }}</div>
          </div>

          <div v-if="loading" class="text-muted py-5 text-center">{{ $t('common.loading_calendar') }}</div>
          <div v-else class="cal-grid">
            <button
              v-for="cell in calendarCells"
              :key="cell.key"
              type="button"
              class="cal-cell"
              :class="{
                'is-outside': !cell.inMonth,
                'is-today': cell.isToday,
                'is-selected': cell.key === selectedDayKey,
                'has-posts': cell.posts.length > 0,
              }"
              @click="selectDay(cell)"
            >
              <div class="cal-cell__top">
                <span class="cal-cell__day">{{ cell.dayNum }}</span>
                <span v-if="cell.posts.length" class="cal-cell__count">{{ cell.posts.length }}</span>
              </div>
              <div class="cal-cell__events">
                <div
                  v-for="post in cell.posts.slice(0, 3)"
                  :key="post.id"
                  class="cal-event"
                  :class="'tone-' + statusTone(post.status)"
                >
                  <span class="cal-event__time">{{ formatTime(post) }}</span>
                  <span class="cal-event__label">{{ post.type }}</span>
                </div>
                <div v-if="cell.posts.length > 3" class="cal-more">{{ $t('calendar.more', { count: cell.posts.length - 3 }) }}</div>
              </div>
            </button>
          </div>
        </ContentCard>
      </div>

      <div class="col-lg-4 mb-30">
        <ContentCard>
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h5 class="mb-0">{{ selectedDayLabel }}</h5>
              <small class="text-muted">{{ selectedDayPosts.length }} {{ uploadCountLabel }}</small>
            </div>
            <router-link
              class="btn btn-sm btn-outline-primary"
              :to="{ path: '/posts/create', query: { schedule: selectedDayKey } }"
            >
              {{ $t('calendar.add') }}
            </router-link>
          </div>

          <div v-if="selectedDayPosts.length === 0" class="cal-empty text-center py-4">
            <i class="fas fa-calendar-day fa-2x mb-3 text-muted"></i>
            <p class="text-muted mb-0">{{ $t('calendar.empty_day') }}</p>
          </div>
          <div v-else class="cal-day-list">
            <router-link
              v-for="post in selectedDayPosts"
              :key="post.id"
              :to="`/posts/${post.id}`"
              class="cal-day-item"
            >
              <div class="cal-day-item__media">
                <img v-if="isImage(post)" :src="post.media_urls[0]" alt="" />
                <div v-else class="cal-day-item__ph"><i :class="typeIcon(post.type)"></i></div>
              </div>
              <div class="cal-day-item__body">
                <div class="cal-day-item__meta">
                  <strong>{{ formatTime(post) }}</strong>
                  <span class="badge badge-light text-capitalize">{{ post.type }}</span>
                  <span class="badge" :class="'badge-' + statusTone(post.status)">{{ statusLabel(post.status) }}</span>
                </div>
                <div class="cal-day-item__caption">{{ truncate(post.caption, 70) }}</div>
                <div class="cal-day-item__user">@{{ post.social_account?.username || '-' }}</div>
              </div>
            </router-link>
          </div>
        </ContentCard>

        <ContentCard card-class="mt-3">
          <h6 class="mb-3">{{ $t('calendar.legend') }}</h6>
          <div class="cal-legend">
            <span><i class="cal-dot tone-primary"></i> {{ $t('calendar.legend_scheduled') }}</span>
            <span><i class="cal-dot tone-success"></i> {{ $t('calendar.legend_published') }}</span>
            <span><i class="cal-dot tone-secondary"></i> {{ $t('calendar.legend_draft') }}</span>
            <span><i class="cal-dot tone-danger"></i> {{ $t('calendar.legend_failed') }}</span>
          </div>
        </ContentCard>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

export default {
  mixins: [i18nPage],
  data() {
    const now = new Date()
    return {
      viewYear: now.getFullYear(),
      viewMonth: now.getMonth(),
      selectedDayKey: this.dayKey(now),
    }
  },
  computed: {
    ...mapGetters('posts', ['posts', 'loading']),
    weekdayNames() {
      return [0, 1, 2, 3, 4, 5, 6].map((d) => this.$t(`common.days_short.${d}`))
    },
    uploadCountLabel() {
      return this.selectedDayPosts.length === 1 ? this.$t('common.upload') : this.$t('common.uploads')
    },
    monthLabel() {
      return new Date(this.viewYear, this.viewMonth, 1).toLocaleDateString(this.intlLocale, {
        month: 'long',
        year: 'numeric',
      })
    },
    calendarPosts() {
      return (this.posts || []).filter((p) => p.scheduled_at || p.published_at)
    },
    calendarCells() {
      const first = new Date(this.viewYear, this.viewMonth, 1)
      const startPad = first.getDay()
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate()
      const prevDays = new Date(this.viewYear, this.viewMonth, 0).getDate()
      const todayKey = this.dayKey(new Date())
      const cells = []

      for (let i = startPad - 1; i >= 0; i--) {
        const dayNum = prevDays - i
        const date = new Date(this.viewYear, this.viewMonth - 1, dayNum)
        cells.push(this.buildCell(date, false, todayKey))
      }
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(this.viewYear, this.viewMonth, day)
        cells.push(this.buildCell(date, true, todayKey))
      }
      while (cells.length % 7 !== 0 || cells.length < 42) {
        const overflow = cells.length - (startPad + daysInMonth) + 1
        const date = new Date(this.viewYear, this.viewMonth + 1, overflow)
        cells.push(this.buildCell(date, false, todayKey))
        if (cells.length >= 42) break
      }
      return cells
    },
    selectedDayPosts() {
      return this.postsForKey(this.selectedDayKey)
    },
    selectedDayLabel() {
      const [y, m, d] = this.selectedDayKey.split('-').map(Number)
      return new Date(y, m - 1, d).toLocaleDateString(this.intlLocale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      })
    },
  },
  async created() {
    await this.fetchPosts('')
  },
  methods: {
    ...mapActions('posts', ['fetchPosts']),
    dayKey(date) {
      const d = new Date(date)
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    postKey(post) {
      const raw = post.scheduled_at || post.published_at
      return raw ? this.dayKey(raw) : null
    },
    postsForKey(key) {
      return this.calendarPosts
        .filter((p) => this.postKey(p) === key)
        .sort((a, b) => {
          const ta = new Date(a.scheduled_at || a.published_at || 0).getTime()
          const tb = new Date(b.scheduled_at || b.published_at || 0).getTime()
          return ta - tb
        })
    },
    buildCell(date, inMonth, todayKey) {
      const key = this.dayKey(date)
      return {
        key,
        date,
        dayNum: date.getDate(),
        inMonth,
        isToday: key === todayKey,
        posts: this.postsForKey(key),
      }
    },
    selectDay(cell) {
      this.selectedDayKey = cell.key
      if (!cell.inMonth) {
        this.viewYear = cell.date.getFullYear()
        this.viewMonth = cell.date.getMonth()
      }
    },
    shiftMonth(delta) {
      const d = new Date(this.viewYear, this.viewMonth + delta, 1)
      this.viewYear = d.getFullYear()
      this.viewMonth = d.getMonth()
    },
    goToday() {
      const now = new Date()
      this.viewYear = now.getFullYear()
      this.viewMonth = now.getMonth()
      this.selectedDayKey = this.dayKey(now)
    },
    formatTime(post) {
      const raw = post.scheduled_at || post.published_at
      if (!raw) return '-'
      return new Date(raw).toLocaleTimeString(this.intlLocale, { hour: '2-digit', minute: '2-digit' })
    },
    statusTone(status) {
      return ({
        scheduled: 'primary',
        published: 'success',
        draft: 'secondary',
        failed: 'danger',
        publishing: 'info',
      })[status] || 'secondary'
    },
    typeIcon(type) {
      if (type === 'story') return 'fas fa-circle'
      if (type === 'reel') return 'fas fa-film'
      return 'fas fa-image'
    },
    isImage(post) {
      const url = post.media_urls?.[0] || ''
      return url && !/\.(mp4|mov)(\?|$)/i.test(url)
    },
    truncate(text, len) {
      if (!text) return this.$t('common.no_caption')
      return text.length > len ? `${text.slice(0, len)}…` : text
    },
  },
}
</script>

<style scoped>
.cal-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}
.cal-toolbar__title {
  text-align: center;
}
.cal-toolbar__title .btn-link {
  color: var(--wc-primary);
  font-size: 0.8rem;
}

.cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.35rem;
  margin-bottom: 0.35rem;
}
.cal-weekday {
  text-align: center;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--wc-text-muted);
  padding: 0.35rem 0;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.35rem;
}
.cal-cell {
  min-height: 110px;
  border: 1px solid #eef1f4;
  border-radius: 10px;
  background: #fff;
  padding: 0.45rem;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  display: flex;
  flex-direction: column;
}
.cal-cell:hover { border-color: var(--wc-primary); }
.cal-cell.is-outside {
  background: #fafbfc;
  opacity: 0.55;
}
.cal-cell.is-today {
  background: #f3fffb;
}
.cal-cell.is-selected {
  border-color: var(--wc-primary);
  box-shadow: 0 0 0 2px rgba(var(--wc-primary-rgb), 0.18);
}
.cal-cell__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.35rem;
}
.cal-cell__day {
  font-size: 0.85rem;
  font-weight: 700;
  color: #2c3340;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
.cal-cell.is-today .cal-cell__day {
  background: var(--wc-primary);
  color: #fff;
}
.cal-cell__count {
  font-size: 0.65rem;
  font-weight: 700;
  color: var(--wc-primary);
  background: rgba(var(--wc-primary-rgb), 0.12);
  border-radius: 999px;
  padding: 0.1rem 0.4rem;
}
.cal-cell__events {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  min-height: 0;
}
.cal-event {
  display: flex;
  gap: 0.25rem;
  align-items: center;
  font-size: 0.65rem;
  line-height: 1.2;
  padding: 0.15rem 0.3rem;
  border-radius: 4px;
  background: #f1f3f5;
  color: #495057;
  overflow: hidden;
  white-space: nowrap;
}
.cal-event.tone-primary { background: rgba(71, 152, 232, 0.15); color: #2b6cb0; }
.cal-event.tone-success { background: rgba(34, 198, 171, 0.15); color: #0f766e; }
.cal-event.tone-danger { background: rgba(239, 110, 110, 0.15); color: #c53030; }
.cal-event.tone-secondary { background: #eef1f4; color: #6c757d; }
.cal-event.tone-info { background: rgba(23, 162, 184, 0.15); color: #0c7489; }
.cal-event__time { font-weight: 700; }
.cal-event__label { text-transform: capitalize; overflow: hidden; text-overflow: ellipsis; }
.cal-more {
  font-size: 0.65rem;
  color: var(--wc-text-muted);
  padding-left: 0.2rem;
}

.cal-day-list { display: flex; flex-direction: column; gap: 0.65rem; }
.cal-day-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.7rem;
  border: 1px solid #eef1f4;
  border-radius: 12px;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.cal-day-item:hover {
  border-color: var(--wc-primary);
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
  text-decoration: none;
  color: inherit;
}
.cal-day-item__media {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f1f3f5;
}
.cal-day-item__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cal-day-item__ph {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--wc-text-muted);
}
.cal-day-item__body { flex: 1; min-width: 0; }
.cal-day-item__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
  margin-bottom: 0.2rem;
  font-size: 0.8rem;
}
.cal-day-item__meta strong { color: var(--wc-primary); }
.cal-day-item__caption {
  font-size: 0.85rem;
  color: #2c3340;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cal-day-item__user {
  font-size: 0.75rem;
  color: var(--wc-text-muted);
  margin-top: 0.15rem;
}

.cal-legend {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  font-size: 0.85rem;
  color: #6c757d;
}
.cal-legend span { display: flex; align-items: center; gap: 0.5rem; }
.cal-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.cal-dot.tone-primary { background: #4798e8; }
.cal-dot.tone-success { background: #22c6ab; }
.cal-dot.tone-secondary { background: #adb5bd; }
.cal-dot.tone-danger { background: #ef6e6e; }

.cal-empty { color: var(--wc-text-muted); }

@media (max-width: 991px) {
  .cal-cell { min-height: 84px; }
  .cal-event__label { display: none; }
}
@media (max-width: 575px) {
  .cal-cell { min-height: 64px; padding: 0.3rem; }
  .cal-event { display: none; }
  .cal-cell.has-posts .cal-cell__count { display: inline-block; }
}
</style>
